/**
 * The titan_remote contract — the constants the remote and its hosts must agree
 * on, in one place. red-app has `@redstor/remote-contract` for the same reason:
 * these values appear in the host's `isCustomElement` compiler option, its
 * templates, its CSS and its Module Federation config, and a literal that drifts
 * on one side fails silently (an unknown element simply never upgrades).
 */

/** The whole app, for the host's content area. */
export const TITAN_REMOTE_ELEMENT_TAG = "titan-remote-element";

/** The side navigation alone, for the host's sidebar. */
export const TITAN_REMOTE_NAV_TAG = "titan-remote-nav";

/**
 * Attribute the HOST toggles on its `<titan-remote-nav>` outlet while its sidebar is collapsed to
 * an icon rail. Present means collapsed; the nav then renders its own collapsed view (icons only,
 * children in a hover flyout) — the same one `cl-sidebar` drives standalone.
 */
export const TITAN_REMOTE_NAV_COLLAPSED_ATTR = "data-collapsed";

/**
 * Global a host sets to `true` BEFORE importing the container, to suppress the
 * remote's own chrome and its sign-in redirect.
 *
 * ⚠️ Deliberately NOT `__SHELL_EMBEDDED__`: red-app already owns that name with
 * its own semantics, and a host may embed both remotes in one document.
 */
export const TITAN_REMOTE_EMBEDDED_FLAG = "__TITAN_REMOTE_EMBEDDED__";

/**
 * Attribute a host sets on its `<html>` element BEFORE importing the container.
 *
 * The remote's page-level CSS (html/body/#app/reset rules) is wrapped in
 * `:where(html:not([data-titan-embedded]))` guards — zero specificity, so
 * standalone rendering is pixel-identical, but in a document carrying this
 * attribute those rules stop matching and the host's own chrome is untouched.
 * `ensureRuntimeEnvironment` sets it as a backstop, but only the host can make
 * the leak window zero: the CSS is injected the moment the container is
 * imported, before any remote code runs.
 *
 * Titan-namespaced for the same reason as the flag above.
 */
export const TITAN_REMOTE_EMBEDDED_ATTR = "data-titan-embedded";

/**
 * CustomEvent the app dispatches on its host element whenever its breadcrumb trail changes.
 * The HOST draws the trail (one component for every app it embeds); Titan only says what is in
 * it. Same event name red-app publishes on — the host listens for one name from any remote.
 */
export const TITAN_REMOTE_CRUMBS_CHANGE_EVENT = "crumbs-change";

/**
 * CustomEvent the HOST dispatches on `document` whenever its company switcher changes company.
 * One name for every remote it embeds, so the payload carries both apps' keys — see CompanyScope.
 */
export const TITAN_REMOTE_COMPANY_SCOPE_CHANGE_EVENT = "company-scope-change";

/**
 * Global the host keeps current with the same object, so a remote booting LATER reads the company
 * already selected instead of waiting for the next event. Host-owned, hence not titan-namespaced.
 */
export const TITAN_REMOTE_COMPANY_SCOPE_GLOBAL = "__SHELL_COMPANY_SCOPE__";

/** The host's company scope. `organisationUuid` is Titan's key; `companyId` is red-app's. */
export type CompanyScope = {
  organisationUuid: string;
  companyId?: number;
  name: string;
  /** `partner` is an MSP, `customer` is a customer organisation. */
  type: "partner" | "customer";
};

/**
 * CustomEvent the HOST dispatches on `document` when its own products panel added a product to, or
 * removed one from, a company. One name for every remote; `platform` says whose product it was.
 */
export const TITAN_REMOTE_COMPANY_PRODUCTS_CHANGE_EVENT =
  "company-products-change";

/** The host's products change. `organisationUuid` is Titan's key; `companyId` is red-app's. */
export type CompanyProductsChange = {
  organisationUuid: string;
  companyId?: number;
  /** `thq` is a Cybersecurity product, `red` a Data Protection one. */
  platform: "thq" | "red";
  write: "create" | "delete";
  /** The product id as the host's catalog names it. */
  productId: string;
};

/** Module Federation: exposed as `titan_remote/Component`. */
export const TITAN_REMOTE_EXPOSED_MODULE = "./Component";

/** The container name a host refers to this remote by. */
export const TITAN_REMOTE_CONTAINER_NAME = "titan_remote";

/**
 * Snapshotted at module evaluation, NOT read per call.
 *
 * The host sets the flag before importing the container, so by the time this
 * module is evaluated the answer is already final. Reading it once means the app
 * element and the nav element — separate Vue apps — can never disagree about
 * whether they are embedded, and nothing later in the page's life can flip the
 * app into a different mode half-mounted.
 */
const embedded =
  (globalThis as Record<string, unknown>)[TITAN_REMOTE_EMBEDDED_FLAG] === true;

/**
 * Whether this app is running inside a host shell rather than standalone.
 *
 * Standalone always returns false: nothing in this repo sets the flag.
 */
export function isEmbedded(): boolean {
  return embedded;
}

export default {
  TITAN_REMOTE_ELEMENT_TAG,
  TITAN_REMOTE_NAV_TAG,
  TITAN_REMOTE_NAV_COLLAPSED_ATTR,
  TITAN_REMOTE_EMBEDDED_FLAG,
  TITAN_REMOTE_EMBEDDED_ATTR,
  TITAN_REMOTE_EXPOSED_MODULE,
  TITAN_REMOTE_CONTAINER_NAME,
  TITAN_REMOTE_CRUMBS_CHANGE_EVENT,
  TITAN_REMOTE_COMPANY_SCOPE_CHANGE_EVENT,
  TITAN_REMOTE_COMPANY_SCOPE_GLOBAL,
  TITAN_REMOTE_COMPANY_PRODUCTS_CHANGE_EVENT,
  isEmbedded,
};
